// text-dialog.jsx
import React, { useState, useEffect, useRef } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import VariablePicker from "../../components/VariablePicker"

const ButtonTextDialog = ({
  defaultText = "",
  variables = {},
  buttonElement = null, // The actual button element for additional actions
  onClose,
}) => {
  const [text, setText] = useState(defaultText)
  const dialogRef = useRef(null)
  const inputRef = useRef(null)
  const variablesButtonRef = useRef(null)
  const [showVariablePicker, setShowVariablePicker] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    onClose({ text: text.trim() })
  }

  const handleVariablesClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowVariablePicker(!showVariablePicker)
  }

  const handleVariableSelect = (variable) => {
    setText(variable)
    setShowVariablePicker(false)
  }

  // Focus input when dialog opens
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  // Handle clicking outside dialog
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the variable picker
      if (event.target.closest('[data-variable-picker="true"]')) {
        return
      }

      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (showVariablePicker) {
          setShowVariablePicker(false)
        } else {
          onClose(null)
        }
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose, showVariablePicker])

  // Handle clicking outside variable picker to close it
  useEffect(() => {
    if (!showVariablePicker) return

    const handleClickOutside = (event) => {
      // Don't close if clicking inside the variable picker
      if (event.target.closest('[data-variable-picker="true"]')) {
        return
      }

      // Don't close if clicking the Variables button
      if (variablesButtonRef.current && variablesButtonRef.current.contains(event.target)) {
        return
      }

      // Close the variable picker
      setShowVariablePicker(false)
    }

    // Add a small delay to prevent immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showVariablePicker])

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{ zIndex: 1000 }}
      >
        <div
          ref={dialogRef}
          className="bg-admin_paper rounded-lg shadow-xl w-full max-w-2xl mx-4"
        >
          {/* Dialog Header */}
          <div className="px-6 py-4 border-b border-admin_bordered flex items-center justify-between">
            <Typography variant="admin_h4">
              Edit Button Text
            </Typography>

            <div ref={variablesButtonRef}>
              <Button
                variant="admin_secondary"
                onClick={handleVariablesClick}
                type="button"
                title="Insert Variable"
              >
                Variables
              </Button>
            </div>
          </div>

          {/* Dialog Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="button-text" className="block text-sm font-medium text-admin_text mb-2">
                  Button Text
                </label>

                <Input
                  ref={inputRef}
                  id="button-text"
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Enter button text or select a variable"
                  variant="admin"
                  className="w-full"
                />
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="flex items-center justify-end space-x-3 bg-admin_paper border-t border-admin_bordered rounded-b-lg px-6 py-4">
              <Button
                variant="admin_secondary"
                onClick={() => onClose(null)}
                type="button"
              >
                Cancel
              </Button>

              <Button
                variant="admin_primary"
                type="submit"
                disabled={!text.trim()}
              >
                Apply
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Variable Picker Panel */}
      {showVariablePicker && (
        <div
          className="fixed"
          style={{
            left: variablesButtonRef.current?.getBoundingClientRect().left || 100,
            top: (variablesButtonRef.current?.getBoundingClientRect().bottom || 100) + 5,
            userSelect: 'none',
            zIndex: 1001
          }}
        >
          <VariablePicker
            variables={variables}
            onVariableSelect={handleVariableSelect}
            onClose={() => setShowVariablePicker(false)}
          />
        </div>
      )}
    </>
  )
}

export default ButtonTextDialog